import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Sparkles, Rocket, Calendar, ArrowRight, FileText } from 'lucide-react'
import type { ReleaseNote } from '@/lib/release-notes'

interface ReleaseNotesListProps {
  releaseNotes: ReleaseNote[]
  showFooter?: boolean
}

/**
 * Release notes list component
 *
 * Displays a grid of release note cards with version, date, and excerpt.
 * First item is marked as "Latest" with special styling.
 *
 * @param releaseNotes - Array of release notes to display
 * @param showFooter - Whether to show the footer with release count (default: true)
 */
export function ReleaseNotesList({ releaseNotes, showFooter = true }: ReleaseNotesListProps) {
  return (
    <div className="space-y-10">
      {/* Hero Header */}
      <div className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-3xl" />
        <div className="relative space-y-4 pb-8">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Rocket className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-5xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                Release Notes
              </h1>
            </div>
          </div>
          <p className="text-muted-foreground text-xl max-w-2xl">
            Discover the latest features, improvements, and updates that make Taqat AI Employee Store even better
          </p>
        </div>
      </div>

      {releaseNotes.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg">No release notes available yet</p>
            <p className="text-sm text-muted-foreground mt-2">Check back soon for updates!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {releaseNotes.map((note, index) => (
            <Link key={note.slug} to={`/release-notes/${note.slug}`} className="group">
              <Card className="h-full transition-all duration-300 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-1 hover:border-primary/50 relative overflow-hidden">
                {/* Background gradient effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Latest badge for first item */}
                {index === 0 && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground border-0 shadow-lg">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Latest
                    </Badge>
                  </div>
                )}

                <CardHeader className="relative pb-4">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className="font-mono text-xs">
                      {note.version}
                    </Badge>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {format(new Date(note.date), 'MMM dd, yyyy')}
                    </span>
                  </div>

                  <CardTitle className="text-xl group-hover:text-primary transition-colors leading-tight line-clamp-2">
                    {note.title}
                  </CardTitle>

                  <CardDescription className="line-clamp-3 text-sm leading-relaxed mt-2">
                    {getExcerpt(note.content)}
                  </CardDescription>
                </CardHeader>

                <CardContent className="relative pt-0">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary group-hover:gap-3 transition-all">
                    Read full release notes
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>

                {/* Bottom accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/50 via-primary to-primary/50 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300" />
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Footer info */}
      {showFooter && releaseNotes.length > 0 && (
        <div className="pt-8 border-t">
          <p className="text-center text-sm text-muted-foreground">
            Showing {releaseNotes.length} {releaseNotes.length === 1 ? 'release' : 'releases'}
          </p>
        </div>
      )}
    </div>
  )
}

/**
 * Extracts a brief excerpt from markdown content
 *
 * Removes markdown syntax and returns first meaningful paragraph.
 * Cleans headers, code blocks, bold/italic, links, and emojis.
 *
 * @param content - The markdown content
 * @returns Brief text excerpt (max 150 characters)
 */
function getExcerpt(content: string): string {
  // Remove markdown headers, code blocks, and special characters
  const cleaned = content
    .replace(/^#+\s+.+$/gm, '') // Remove headers
    .replace(/```[\s\S]*?```/g, '') // Remove code blocks
    .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
    .replace(/\*(.+?)\*/g, '$1') // Remove italic
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // Remove links
    .replace(/^[-*]\s+/gm, '') // Remove list markers
    .replace(/^\s*\n/gm, '') // Remove empty lines
    .replace(/🎯|✨|🤖|⚙️|🔗|🐛|🚀|📖/g, '') // Remove emojis
    .trim()

  // Get first meaningful paragraph
  const lines = cleaned.split('\n').filter((line) => line.trim().length > 20)
  const excerpt = lines[0] || 'Click to read the full release notes...'

  return excerpt.length > 150 ? excerpt.substring(0, 150) + '...' : excerpt
}
